"use client";

import * as React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FaqItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}
const FAQ_ITEMS: FaqItem[] = [{
  id: "faq-1",
  question: "Wie buche ich meinen Flughafentransfer in Hurghada?",
  answer: <span>
        Du kannst deinen Transfer direkt online über unsere{" "}
        <Link to="/BookTransferDePage" className="text-primary underline font-medium hover:text-primary/80">
          Buchungsseite
        </Link>{" "}
        oder schnell und unkompliziert per WhatsApp anfragen. Teile uns einfach deine Abholdetails, dein Zielhotel und dein gewünschtes Fahrzeug mit.
      </span>
}, {
  id: "faq-2",
  question: "Was kostet der Transfer vom Flughafen Hurghada zum Hotel?",
  answer: <span>
        Jeder Preis ist ein Festpreis pro Fahrzeug und nicht pro Person, ohne versteckte Zusatzkosten. Der genaue Betrag richtet sich nach deinem Reiseziel und Fahrzeugtyp und wird immer vorab vereinbart (z. B. Hurghada Stadt: ab 768,38 EGP im Pkw oder 1.359,44 EGP im Van; EUR- und USD-Beträge sind ungefähre Umrechnungen). Alle Routen und Preise findest du in unserer{" "}
        <Link to="/DestinationsDEPage" className="text-primary underline font-medium hover:text-primary/80">
          Zielorte-Übersicht
        </Link>.
      </span>
}, {
  id: "faq-3",
  question: "Wie bezahle ich meinen Transfer?",
  answer: "Die Bezahlung erfolgt bei der Ankunft an deinem Hotel ganz bequem in bar. Wir akzeptieren jede Währung; die gewünschte Währung und den genauen Betrag stimmen wir vorab mit dir ab."
}, {
  id: "faq-4",
  question: "Kann ich auch den Rücktransfer zum Flughafen Hurghada buchen?",
  answer: "Ja, du kannst deinen Hin- und Rücktransfer direkt im Voraus buchen oder deine Abholung vom Hotel ganz flexibel per WhatsApp mit unserem Team abstimmen."
}, {
  id: "faq-5",
  question: "Was passiert, wenn mein Flug nach Hurghada Verspätung hat?",
  answer: "Wir verfolgen die Ankunftszeit und mögliche Verspätungen deines Fluges kostenlos. Dein Transfer beinhaltet bis zu 60 Minuten kostenlose Wartezeit ab dem vereinbarten Treffzeitpunkt. Gib bei der Buchung deine Flugnummer an und schreib uns per WhatsApp, wenn sich deine Ankunft stark verschiebt."
}, {
  id: "faq-6",
  question: "Wo treffe ich meinen Fahrer am Flughafen Hurghada?",
  answer: "Dein Fahrer erwartet dich direkt vor den Ausgangstüren des Ankunftsterminals mit einem Namensschild, auf dem dein Name gut lesbar steht."
}, {
  id: "faq-7",
  question: "Gibt es Kindersitze für Kinder?",
  answer: "Ja, Kindersitze sind für Kinder ab einem Jahr kostenlos verfügbar. Gib das Alter deines Kindes einfach direkt bei der Buchung an, damit wir den passenden Sitz bereitstellen können."
}, {
  id: "faq-8",
  question: "Wie wähle ich das passende Fahrzeug für mein Gepäck aus?",
  answer: "Unsere Fahrzeugauswahl zeigt dir die genauen Koffer- und Passagierkapazitäten für jedes Modell. Wenn du mit zusätzlichem Gepäck, Tauchausrüstung oder Golftaschen reist, empfehlen wir dir die Buchung eines Vans."
}, {
  id: "faq-9",
  question: "Kann ich direkt über WhatsApp buchen oder Fragen stellen?",
  answer: "Selbstverständlich. Unser WhatsApp-Team ist rund um die Uhr unter +20 10 10634627 für Sofortangebote, Buchungsbestätigungen und deine Reisebetreuung erreichbar."
}, {
  id: "faq-10",
  question: "Wie weit im Voraus sollte ich meinen Transfer buchen?",
  answer: "Wir empfehlen dir, deinen Transfer mindestens 12 bis 24 Stunden im Voraus zu buchen. Kurzfristige Anfragen sind je nach Verfügbarkeit unserer Fahrzeuge ebenfalls möglich."
}];
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15,
    once: true
  });
  return <section id="faq" data-nav="light" ref={ref} className="bg-secondary text-secondary-foreground py-20 md:py-24 border-t border-border transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-[1136px] mx-auto px-6">
        {/* Header Block with Signature Kit Device */}
        <div className="max-w-2xl mx-auto text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="text-xs uppercase font-semibold tracking-wider text-muted-foreground">
              Hast du Fragen?
            </span>
            {/* Signature hairline rule capped with a crisp turquoise terminal dot */}
            <div className="flex items-center" aria-hidden="true">
              <span className="w-8 h-px bg-border inline-block" />
              <span className="size-1.5 rounded-full bg-primary inline-block shrink-0" />
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-black">
            Häufig gestellte Fragen
          </h2>
        </div>

        {/* 10-Item Accordion container centered in narrative columns 2-11 */}
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {FAQ_ITEMS.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="bg-card text-card-foreground border border-border rounded-md px-5 sm:px-6 shadow-2xs data-[state=open]:border-primary transition-colors">
                <AccordionTrigger className="text-base sm:text-lg font-medium text-foreground py-5 hover:no-underline text-left cursor-pointer group">
                  <span className="pr-4 leading-snug group-hover:text-primary transition-colors">
                    {item.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-5 pt-1">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}